import {Injectable} from "@angular/core"
import {Observable, of, ReplaySubject, throwError} from "rxjs"
import {catchError, distinctUntilChanged, filter, flatMap, map, subscribeOn, tap} from "rxjs/operators"
import {async} from "rxjs/internal/scheduler/async"
import {AuthService} from "app/common/services/auth/AuthService"
import {CurrentUser} from "app/common/services/current-user/models/CurrentUser"
import {ErrorService} from "app/common/services/error/service/ErrorService"
import {Router} from "@angular/router"
import {environment} from "environments/environment"

@Injectable({providedIn: "root"})
export class CurrentUserStore {

    private _loggedIn$ = new ReplaySubject<boolean>(1)
    private _currentUser$ = new ReplaySubject<CurrentUser | null>(1)

    public constructor(private service: AuthService, private errorService: ErrorService, private router: Router) {
        // On App start we'll try and get the User Config
        this.refreshUserConfig()
        this.watchForLogout()
    }

    private watchForLogout() {
        this._loggedIn$
            .pipe(filter((loggedIn) => !loggedIn))
            .subscribe(() => this.router.navigateByUrl(environment.loginUrl))
    }

    public get loggedIn(): Observable<boolean> {
        return this._loggedIn$
                   .pipe(distinctUntilChanged())
                   .pipe(subscribeOn(async))
    }

    public logIn(email: string, password: string): Observable<void> {
        const result = new ReplaySubject<void>(1)
        this.service
            .logIn(email, password)
            .pipe(flatMap(() => this.updateUserConfig()))
            .pipe(tap(() => this._loggedIn$.next(true)))
            .pipe(subscribeOn(async))
            .subscribe(result)
        return result
    }

    public logout() {
        this.service
            .logOut()
            .subscribe(this.clearUser.bind(this))
    }

    public clearUser() {
        this._currentUser$.next(null)
        this._loggedIn$.next(false)
    }

    private updateUserConfig(): Observable<void> {
        return this.service
                   .retrieveUserConfig()
                   .pipe(map((config) => CurrentUser.create(config)))
                   .pipe(flatMap((user) => this.ensureUserAccess(user)))
                   .pipe(catchError(this.handleConfigError.bind(this)))
                   .pipe(map((user) => this._currentUser$.next(user)))
    }

    private handleConfigError(): Observable<null> {
        this._loggedIn$.next(false)
        return of(null)
    }

    private ensureUserAccess(user: CurrentUser): Observable<CurrentUser> {
        if (user.hasFeature("SCREEN_IV_SALES_ADMIN")) {
            return of(user)
        } else {
            const message = "You do not have access to view this site";
            return this.errorService
                       .open(message, "Access Denied")
                       .pipe(tap(() => this.clearUser()))
                       .pipe(flatMap(() => throwError(message)))
        }
    }

    private refreshUserConfig() {
        this.updateUserConfig().subscribe()
    }

    public get currentUser(): Observable<CurrentUser | null> {
        return this._currentUser$.pipe(subscribeOn(async))
    }
}
